import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  Lightbulb,
  GraduationCap,
  Heart,
  Rocket,
  PhoneCall,
  Users,
  Laptop,
  Presentation,
  Wallet,
  Globe,
  TrendingUp,
  Clock,
  ChevronDown,
  FileText,
} from 'lucide-react';

const faqs = [
  {
    question: 'How long does the full process take?',
    answer:
      'The entire process usually takes 3 to 5 days. You\'ll spend 20-30 minutes on the pre-counselling call, 45-60 minutes taking the online assessment, and 45-60 minutes in the final debrief session once the report is generated (within 48 hours).',
  },
  {
    question: 'Can I take the assessment online from home?',
    answer:
      'Yes, absolutely. The psychometric assessment is hosted on our secure Edumilestones portal and can be taken from the comfort of your home. If you prefer, you can also take it at our Lucknow office.',
  },
  {
    question: 'Are group discounts available for schools?',
    answer:
      'Yes, we actively partner with schools and colleges in Lucknow and Balrampur. We offer significant bulk assessment discounts and can conduct on-site career camps. Please contact us directly for institutional pricing.',
  },
  {
    question: 'What makes this different from free career tests online?',
    answer:
      'Free online tests are generic and often only measure one dimension (like interest). Our service utilises a multi-dimensional, scientifically validated psychometric battery measuring Aptitude, Personality, Interests, Multiple Intelligences, and Learning Styles. Most importantly, it includes an expert human debrief with a former Govt. of India DDG.',
  },
  {
    question: 'Is this suitable for working professionals?',
    answer:
      'Yes. We have specific assessment batteries designed for mid-career professionals looking to pivot, seeking leadership roles, or wanting to understand their career dissatisfaction.',
  },
  {
    question: 'What happens after the debrief session?',
    answer:
      'You walk away with a concrete action plan. We also provide 30 days of email Q&A support. If you require further ongoing guidance, you can book optional follow-up sessions at just Rs. 500 per session.',
  },
];

const benefits = [
  {
    icon: <PhoneCall size={32} />,
    title: 'Informed Career Decisions',
    desc: 'Career counselling helps to make informed career decisions.',
  },
  {
    icon: <GraduationCap size={32} />,
    title: 'Clear Education and Career Road Map',
    desc: 'Students will get a clear educational and career road map. Early-stage career counselling always gives more time for execution.',
  },
  {
    icon: <Heart size={32} />,
    title: 'Career Satisfaction',
    desc: 'Think about to pursue a career that you love to. Career counselor explores your interest areas, personality and abilities and based on that finds out most suitable career option for you.',
  },
  {
    icon: <Rocket size={32} />,
    title: 'Career Opportunities',
    desc:
      'Today a lot of career opportunities and new aged career options are available. Most of the students and professionals are not aware of these career opportunities, latest trends, salary packages and much more crucial information that plays an important role to select a career option. Trained career counselors always update themselves for all such crucial information and systematically guide students to make the right career decision. A career in data science, a career in artificial intelligence, a career in digital marketing and many newer aged career options.',
  },
];

const stages = [
  'Career counselling for 5th class to find out your multiple intelligence, aptitude and learning style',
  'Career Counselling for 10th class to find out best subject streams after 10th and most suitable career path',
  'Career Counselling for 12th students to find out the best career option after 12th and education road map',
  'Career Counselling for Graduates to find out the best career option after graduation with higher studies education road map.',
  'Career Counselling for professionals for early and mid-career shift planning.',
];

const processSteps = [
  {
    icon: <PhoneCall size={32} />,
    title: 'Book Your Session',
    desc: 'Reach out via phone (+91 9335879877), email, or our online portal to schedule your process. Prepare basic details like your name, age, current class/profession, and primary concern.',
    meta: 'Mon–Fri 5PM–8PM | Sun 10AM–2:30PM',
  },
  {
    icon: <Users size={32} />,
    title: 'Pre-Counselling Conversation',
    desc: 'A 20–30 minute conversation before the test with CCA Arendra Nath Sharma himself. This is not a test — it\'s a listening session to understand your background, concerns, and family expectations.',
    meta: 'Duration: 20-30 mins',
  },
  {
    icon: <Laptop size={32} />,
    title: 'Psychometric Assessment',
    desc: 'Take the scientifically validated online assessment via our Edumilestones portal. It evaluates Aptitude, Personality Type, Interest Clusters, Multiple Intelligence, and Learning Styles.',
    meta: 'At home or office | 30-60 mins',
  },
  {
    icon: <FileText size={32} />,
    title: 'Report Generation',
    desc: 'Our system analyses your responses and generates a comprehensive 20+ page report detailing top career clusters, path recommendations, strengths, and development areas.',
    meta: 'Generated in 24-48 hours',
  },
  {
    icon: <Presentation size={32} />,
    title: 'Report Debrief Session',
    desc: 'A 1-on-1 debrief with the founder to walk through every page of your report. Parents/guardians are highly encouraged to attend for student sessions. We define actionable next steps.',
    meta: 'Duration: 45-60 mins',
  },
  {
    icon: <Wallet size={32} />,
    title: 'Additional Follow-Up Meetings',
    desc: 'If you require further guidance after the initial sessions, additional meetings can be scheduled as needed. An additional charge of Rs 500 will be charged for each extra meeting beyond the initial sessions.',
    meta: 'Optional: Rs 500 per extra meeting',
  },
];

const HowItWorks = () => {
  const [openFaq, setOpenFaq] = useState(null);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
    >
      <section className="HowItWorks_hero__xdE-z">
        <div className="container HowItWorks_heroContainer__RzHtg">
          <motion.span
            className="section-label"
            style={{ color: 'var(--c-gold)' }}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            CAREER COUNSELLING
          </motion.span>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            Why It Matters
          </motion.h1>
          <motion.p
            className="HowItWorks_heroSubtext__jcdb4"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
          >
            A transparent, six-step journey from confusion to absolute career clarity.
          </motion.p>
        </div>
      </section>

      <section className="section">
        <div className="container">
          <div className="HowItWorks_sectionHeader__wsjbk">
            <span className="section-label">THE VISION FOR GUIDANCE</span>
            <h2>Why is career counselling important?</h2>
          </div>
          <p className="HowItWorks_sectionIntro__c3ijK">
            Career counselling provides better understanding and clarity about the career. It saves a lot of time, money and avoids frustration caused due to career indecisiveness. A career counselor not only helps the student to find out the most suitable career option but also provide a detailed execution plan.
          </p>
          <div className="HowItWorks_benefitGrid__tMTeQ">
            {benefits.map((benefit, index) => (
              <div key={index} className="card">
                <div className="HowItWorks_benefitIconWrap__1HaBE">{benefit.icon}</div>
                <h3>{benefit.title}</h3>
                <p>{benefit.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="section section-alt">
        <div className="container">
          <div className="HowItWorks_sectionHeader__wsjbk">
            <span className="section-label">ACCESSIBLE ACROSS INDIA</span>
            <h2>Why Online Career Counselling?</h2>
          </div>
          <div className="HowItWorks_contentBlock__GqR6o">
            <Globe size={40} className="HowItWorks_contentIcon__IimQp" />
            <div>
              <p>
                Online career counselling is the need of the hour. With more than 260 million students, India consists of the majority of the youth population. Nowadays many students are confused about their career paths. We at Vitae Careers often realize that their problems can be addressed with appropriate online career counselling or face to face career counselling. For all the students who are confused about their career path, it would be a wise decision to take a career counselling session with a trained career counselor.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="section">
        <div className="container">
          <div className="HowItWorks_sectionHeader__wsjbk">
            <h2>Stages Where Career Guidance Is Needed</h2>
          </div>
          <ul className="HowItWorks_stageList__7Z09w">
            {stages.map((stage, index) => (
              <li key={index} className="HowItWorks_stageItem__Sf2fS">
                <span className="HowItWorks_stageDot__POryL">{index + 1}</span>
                <span>{stage}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section className="section section-alt">
        <div className="container">
          <div className="HowItWorks_sectionHeader__wsjbk">
            <span className="section-label">WORKING PROFESSIONALS</span>
            <h2>Online Career Counselling for Working Professionals</h2>
          </div>
          <div className="HowItWorks_contentBlock__GqR6o">
            <TrendingUp size={40} className="HowItWorks_contentIcon__IimQp" />
            <div>
              <p>
                According to Indeed survey, 40% of the working professionals are not satisfied with their jobs. A large portion of professionals is planning to switch their careers. Mostly the reason is not their abilities but career interest. Online career counselling for working professionals can bridge this gap. It will help working professionals to find out best next move or shift in their career with a detailed execution plan for the next 10 years. Online career counselling for working professionals is different than student career counselling and it is delivered by an expert mentor. Vitae Careers extends recognized career counselling solutions for working professionals.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="section">
        <div className="container">
          <div className="HowItWorks_sectionHeader__wsjbk">
            <span className="section-label">OUR PROCESS</span>
            <h2>The Assessment Process — Step by Step</h2>
          </div>
          <div className="HowItWorks_timeline__z3ryC">
            {processSteps.map((step, index) => (
              <div key={index} className="HowItWorks_timelineItem__lldv+">
                <div className="HowItWorks_timelineContent__PPXq9">
                  <div className="HowItWorks_stepNumber__s61gM">Step 0{index + 1}</div>
                  <h3>{step.title}</h3>
                  <p>{step.desc}</p>
                  <span className="HowItWorks_stepMeta__6XxFQ">{step.meta}</span>
                </div>
                <div className="HowItWorks_timelineCenter__ghFPQ">
                  <div className="HowItWorks_timelineIcon__IohoE">{step.icon}</div>
                  {index !== processSteps.length - 1 && <div className="HowItWorks_timelineLine__HQ92F"></div>}
                </div>
                <div className="HowItWorks_timelineEmpty__QFY46"></div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="section section-alt">
        <div className="container HowItWorks_faqContainer__mYvVD">
          <div className="HowItWorks_sectionHeader__wsjbk">
            <span className="section-label">HAVE QUESTIONS?</span>
            <h2>Frequently Asked Questions</h2>
          </div>
          <div className="HowItWorks_faqList__t-HWq">
            {faqs.map((faq, index) => (
              <div key={index} className={`HowItWorks_faqItem__Ebzaq ${openFaq === index ? 'HowItWorks_faqOpen__rxYx0' : ''}`}>
                <button
                  className="HowItWorks_faqQuestion__ATNdf"
                  onClick={() => setOpenFaq(openFaq === index ? null : index)}
                  aria-expanded={openFaq === index}
                >
                  {faq.question}
                  <ChevronDown
                    size={20}
                    className={`HowItWorks_faqIcon__MKZZ2 ${openFaq === index ? 'HowItWorks_faqIconRotated__kOg5C' : ''}`}
                  />
                </button>
                <AnimatePresence>
                  {openFaq === index && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3 }}
                      className="HowItWorks_faqAnswerWrap__DqtpD"
                    >
                      <div className="HowItWorks_faqAnswer__6tqa7">{faq.answer}</div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="section HowItWorks_blogCtaSection">
        <div className="container" style={{ textAlign: 'center' }}>
          <Link to="/blog" className="btn-primary">
            Click here for blog
          </Link>
        </div>
      </section>
    </motion.div>
  );
};

export default HowItWorks;
